'use client';

import { Modal, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgAdd, SvgLink } from '@common/icon';
import { cn } from '@core/utils';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { BREAKPOINTS } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useClipboard } from '@/hooks/useClipboard';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

const OG_IMAGE_URL =
  'https://cdn.welfarehello.com/static/event/home-love/market/kakao-og-sns.png';
const SHARE_URL =
  'https://www.welfarehello.com/donation/event/market/sns?utm_source=wello&utm_medium=event_button&utm_campaign=sns_review';

interface SnsShareDrawerProps {
  opened: boolean;
  onClose?: () => void;
}

export const SnsShareDrawer = ({ opened, onClose }: SnsShareDrawerProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const [webviewType, osType] = useDeviceInfoStore(
    useShallow((state) => [state.webviewType, state.osType]),
  );

  const { isWelloApp } = useCheckWelloApp();
  const { isInitialized, shareToKakao } = useKakaoShare();

  const title = '웰로 고향사랑기부제 SNS 후기 이벤트';
  const description =
    'SNS에 웰로 고향사랑기부 인증하고 최대 1만원 웰포인트까지 받아보세요!';

  const copy = useClipboard();

  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED,
  });

  const { request: requestShare, isPending } = useHandleApp({
    type: MESSAGE_KEYWORD.SHARE_CONTENT,
  });

  const { toast } = useToast();

  const isMobileWeb =
    !webviewType && osType && ['ios', 'android'].includes(osType);

  useEffect(() => {
    if (opened) {
      checkKakaoInstallRequest();
    }
  }, [checkKakaoInstallRequest, opened]);

  const handleKakaoClickInApp = async () => {
    if (isPending) return;

    if (checkKakaoInstallResponse?.isSuccess) {
      requestShare({
        shareType: 'kakao',
        url: SHARE_URL,
      });
      onClose?.();
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail',
      });
    }
  };

  const handleKakaoClickInWeb = async () => {
    shareToKakao(
      SHARE_URL,
      title,
      description,
      OG_IMAGE_URL,
      '이벤트 참여하기',
    );
    onClose?.();
  };

  const content = (
    <div
      className={cn('mx-42 flex justify-around gap-6', {
        'justify-between': isWelloApp,
      })}
    >
      {webviewType !== WEBVIEW_TYPE.KB_PAY ? (
        <KakaoButton
          isLoading={!isInitialized}
          onClick={isWelloApp ? handleKakaoClickInApp : handleKakaoClickInWeb}
        />
      ) : null}
      <button
        type="button"
        className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
        onClick={() => {
          try {
            copy({ value: SHARE_URL });
          } finally {
            onClose?.();
          }
        }}
      >
        <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-500 text-Gray-white">
          <SvgLink />
        </div>
        <h4 className="font-400 text-Gray-900 typo-body4">URL 복사</h4>
      </button>
      {isWelloApp || isMobileWeb ? (
        <button
          type="button"
          className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
          onClick={() => {
            try {
              if (isWelloApp) {
                return requestShare({
                  shareType: 'uni',
                  url: SHARE_URL,
                });
              }

              if (isMobileWeb) {
                return navigator.share({
                  url: SHARE_URL,
                });
              }
            } finally {
              onClose?.();
            }
          }}
        >
          <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-200 text-Gray-black">
            <SvgAdd />
          </div>
          <h4 className="font-400 text-Gray-900 typo-body4">더 보기</h4>
        </button>
      ) : null}
    </div>
  );

  if (isDesktopViewport) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        asChild
        className="w-full max-w-[480px]"
        closeButton
      >
        <div>{content}</div>
      </Modal>
    );
  }

  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      drawerType="bottom"
      drawerNoPadding
    >
      <div className="pb-16">{content}</div>
    </ResponsiveDialog>
  );
};

interface KakaoButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

const KakaoButton = ({ isLoading, onClick }: KakaoButtonProps) => {
  return (
    <button
      type="button"
      disabled={isLoading}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-6 border-none',
        { 'cursor-not-allowed opacity-50': isLoading },
      )}
      onClick={onClick}
    >
      <div className="flex size-48 items-center justify-center rounded-pill bg-[#fbdf03]">
        {isLoading ? (
          <Spinner size={24} />
        ) : (
          <Image
            alt="kakao"
            height={24}
            src="/assets/pngs/login/kakao.png"
            width={24}
          />
        )}
      </div>
      <span className="font-400 text-Gray-900 typo-body4">카카오톡</span>
    </button>
  );
};
